import styled from '@emotion/styled'
import { symbolCurrency } from '@lbs/currency'
import { TCards } from '@lbs/shared-rp-types'
import { HAND_COMBINATIONS_LABELS } from '@lbs/shared-rp-types/dist/payout'
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DiamondIcon from '@mui/icons-material/Diamond'
import DragHandleIcon from '@mui/icons-material/DragHandle'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import PaymentsIcon from '@mui/icons-material/Payments'
import PersonIcon from '@mui/icons-material/Person'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import VerifiedIcon from '@mui/icons-material/Verified'
import {
  Box,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ApiPlayers } from 'api'
import { queryKeys } from 'constants/EQueryKeysConstant'
import { Duration, format, intervalToDuration } from 'date-fns'
import { observer } from 'mobx-react-lite'
import { Prp2boxApiGames } from 'prp2boxApi'
import { FC, ImgHTMLAttributes } from 'react'
import { useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import routes from 'router/routes'

type CardPicProps = ImgHTMLAttributes<HTMLImageElement> & {
  value: TCards
  className?: string
}

const Img = styled.img`
  display: block;
  border: 1px solid grey;
`

const CardImg = styled.img`
  width: 41px;
  height: 65px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  display: block;
`

export const CardPic: FC<CardPicProps> = ({ value, className, ...rest }) => {
  const url = `${process.env.PUBLIC_URL}/cards/${value}.svg`

  return (
    <div className={className}>
      <Img src={url} alt={value} {...rest} />
    </div>
  )
}

const BoxPlayerLogin: FC<{ playerId?: string }> = ({ playerId }) => {
  const { data: player } = useQuery(['player', playerId], () => ApiPlayers.getPlayerById(playerId!), {
    enabled: !!playerId,
  })

  if (!player) return null

  return (
    <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
      Игрок: {player.login}
    </Typography>
  )
}

const SessionPlayers: FC<{ playerIds: string[] }> = ({ playerIds }) => {
  const playerQueries = useQuery(
    ['session-players', playerIds.join(',')],
    async () => {
      const players = await Promise.all(playerIds.map((id) => ApiPlayers.getPlayerById(id)))
      return players
    },
    {
      enabled: playerIds.length > 0,
      staleTime: 30_000,
    }
  )

  if (!playerQueries.data || playerQueries.data.length === 0) {
    return <Typography variant="body1">—</Typography>
  }

  return <Typography variant="body1">{playerQueries.data.map((player) => player.login).join(', ')}</Typography>
}

const RpGameSessionDetails: FC = observer(() => {
  const params = useParams<{ gameId: string; sessionId: string }>()
  const navigate = useNavigate()

  const { data: game } = useQuery(queryKeys.rpGames.byId(Number(params.gameId)), () =>
    Prp2boxApiGames.getById(Number(params.gameId))
  )

  const { data: session } = useQuery(
    queryKeys.rpGames.sessionById(Number(params.gameId), Number(params.sessionId)),
    () => Prp2boxApiGames.getSessionNavigationByIds(Number(params.gameId), Number(params.sessionId)),
    {
      enabled: !!params.gameId && !!params.sessionId,
      onError: (error) => {
        toast.error(`${error}`)
      },
    }
  )

  const { data: boxes } = useQuery(
    queryKeys.rpGames.boxesById(Number(params.gameId), Number(params.sessionId)),
    () => Prp2boxApiGames.getBoxesByIds(Number(params.gameId), Number(params.sessionId)),
    {
      enabled: !!params.gameId && !!params.sessionId,
      onError: (error) => {
        toast.error(`${error}`)
      },
    }
  )

  if (!game) {
    return <Typography variant="h6">Игра не найдена</Typography>
  }

  if (!session) {
    return <Typography variant="h6">Сессия не найдена</Typography>
  }

  if (!boxes) {
    return <Typography variant="h6">Места не найдены</Typography>
  }

  const uniquePlayerIds = Array.from(
    new Set(boxes.rows.map((box) => box.data.player?.idHex).filter((id): id is string => !!id))
  )

  // Расчет профита
  const gameRatio = boxes.rows.find((box) => box.data.playerFinancials)?.data.playerFinancials?.gameRatio || 0
  const currency = boxes.rows.find((box) => box.data.playerFinancials)?.data.playerFinancials?.currency || 'USD'
  const profitInCurrency = session.sessionNet * gameRatio

  const getProfitColor = (value: number) => {
    if (value === 0) return 'text.primary'
    if (value > 0) return 'success.main'
    return 'error.main'
  }

  // const getProfitSign = (value: number) => {
  //   if (value > 0) return '+'
  //   if (value < 0) return '−'
  //   return ''
  // }

  const duration = intervalToDuration({ start: 0, end: session.durationSec * 1000 })

  const formatCustomDuration = (dur: Duration) => {
    const parts: string[] = []
    if (dur.days) parts.push(`${dur.days} дн`)
    if (dur.hours) parts.push(`${dur.hours} ч`)
    if (dur.minutes) parts.push(`${dur.minutes} мин`)
    if (dur.seconds) parts.push(`${dur.seconds} сек`)
    return parts.join(' ') || '0 сек'
  }

  const txtDuration = formatCustomDuration(duration)

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return format(date, 'dd/MM/yyyy HH:mm:ss')
  }

  const diamondIconSx = { color: 'text.primary', opacity: 0.38, verticalAlign: 'middle', ml: 0.5 }

  const handlePreviousSession = () => {
    if (session?.navigation?.previousSessionId) {
      navigate(
        routes.prpSessionDetails
          .replace(':gameId', params.gameId!)
          .replace(':sessionId', String(session.navigation.previousSessionId))
      )
    }
  }

  const handleNextSession = () => {
    if (session?.navigation?.nextSessionId) {
      navigate(
        routes.prpSessionDetails
          .replace(':gameId', params.gameId!)
          .replace(':sessionId', String(session.navigation.nextSessionId))
      )
    }
  }

  const getNetWinColor = (value: number) => {
    if (value === 0) return 'text.primary'
    if (value > 0) return 'success.main'
    return 'error.main'
  }

  const formatFinancialValue = (value: number): string => {
    if (value === 0) return '0'
    if (value > 0) return `+${value.toLocaleString()}`
    return value.toLocaleString()
  }

  return (
    <Box>
      <CardContent
        component={Paper}
        sx={{
          marginBottom: 2,
          pb: 2,
          pt: 2,
        }}>
        <Grid container spacing={2.5} alignItems="center" mb={2}>
          <Grid item>
            <Typography variant="h5" component="span">
              Russian Poker / Сессия №{session.id}
            </Typography>
          </Grid>
          <Grid item>
            <Chip
              label={`ID: ${session.publicId}`}
              size="small"
              sx={{
                backgroundColor: '#e0e0e0',
                color: 'text.primary',
              }}
            />
          </Grid>
          <Grid item>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountCircleIcon sx={{ color: 'text.primary', opacity: 0.7 }} />
              <SessionPlayers playerIds={uniquePlayerIds} />
            </Stack>
          </Grid>
          <Grid item sx={{ marginLeft: 'auto' }}>
            <Stack direction="row" spacing={2.5} alignItems="center">
              {session?.navigation && (
                <Typography variant="caption" color="text.secondary">
                  {session.navigation.currentIndex} из {session.navigation.totalSessions}
                </Typography>
              )}
              <IconButton
                size="small"
                disabled={!session?.navigation?.previousSessionId}
                onClick={handlePreviousSession}
                sx={{
                  '&.Mui-disabled': {
                    opacity: 0.38,
                  },
                }}>
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                size="small"
                disabled={!session?.navigation?.nextSessionId}
                onClick={handleNextSession}
                sx={{
                  '&.Mui-disabled': {
                    opacity: 0.38,
                  },
                }}>
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} lg={6}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Начало"
                  value={formatDateTime(session.startedAt)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonthIcon sx={{ color: 'text.primary', opacity: 0.38 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Конец"
                  value={formatDateTime(session.endedAt)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonthIcon sx={{ color: 'text.primary', opacity: 0.38 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Длительность"
                  value={txtDuration}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeFilledIcon sx={{ color: 'text.primary', opacity: 0.38 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Grid container spacing={2.5}>
              <Grid item xs={0} sm={6} />
              <Grid item xs={12} sm={3}>
                <TextField
                  value={formatFinancialValue(session.sessionNet)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <DiamondIcon sx={{ color: getProfitColor(session.sessionNet), opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                    sx: {
                      color: getProfitColor(session.sessionNet),
                      fontWeight: 'bold',
                      fontFamily: 'monospace',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  value={formatFinancialValue(profitInCurrency)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: 'text.primary' }}>
                          {symbolCurrency[currency as keyof typeof symbolCurrency]}
                        </Typography>
                      </InputAdornment>
                    ),
                    sx: {
                      color: getProfitColor(profitInCurrency),
                      fontWeight: 'bold',
                      fontFamily: 'monospace',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>

      <CardContent component={Paper}>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <PersonIcon />
          <Typography variant="h6">Дилер</Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2.5} mt={2}>
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2.5} alignItems="center">
                <VerifiedIcon />
                <Typography variant="h6">Результат</Typography>
              </Stack>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Комбинация:{' '}
                  <Typography component="span" fontWeight="bold">
                    {session.dealer.combination === 'HIGH_CARD'
                      ? 'Нет игры'
                      : HAND_COMBINATIONS_LABELS.ru[session.dealer.combination] || '—'}
                  </Typography>
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {session.dealer.cards.map((card) => (
                  <CardImg key={`dealer-${card}`} src={`${process.env.PUBLIC_URL}/cards/${card}.svg`} alt={card} />
                ))}
              </Stack>
            </Stack>
          </Grid>

          {session.buyGameDetails && (
            <>
              <Grid item sx={{ display: 'flex' }}>
                <Divider orientation="vertical" flexItem />
              </Grid>

              <Grid item xs={12} md>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2.5} alignItems="center">
                    <ShoppingCartIcon />
                    <Typography variant="h6">Покупка игры дилеру</Typography>
                  </Stack>

                  <Typography variant="caption" color="text.secondary">
                    Попыток:{' '}
                    <Typography component="span" fontWeight="bold">
                      {session.buyGameDetails.totalAttempts}
                    </Typography>
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="flex-end" flexWrap="wrap" useFlexGap>
                    {session.buyGameDetails.handBeforeBuy.cards.map((card) => (
                      <CardImg
                        key={`before-buy-${card}`}
                        src={`${process.env.PUBLIC_URL}/cards/${card}.svg`}
                        alt={card}
                      />
                    ))}
                    <ChevronRightIcon sx={{ color: 'text.secondary', mx: 0.5, mb: 2 }} />
                    <CardImg
                      src={`${process.env.PUBLIC_URL}/cards/${session.buyGameDetails.replacedCard}.svg`}
                      alt={session.buyGameDetails.replacedCard}
                      style={{ opacity: 0.4 }}
                    />
                    {session.buyGameDetails.matchingRankCards.length > 0 && (
                      <>
                        <ChevronRightIcon sx={{ color: 'primary.main', mx: 0.5, mb: 2 }} />
                        {session.buyGameDetails.matchingRankCards.map((card) => (
                          <Box key={`matching-${card}`} sx={{ position: 'relative' }}>
                            <DragHandleIcon
                              sx={{
                                position: 'absolute',
                                top: -16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                color: 'primary.main',
                                fontSize: 16,
                              }}
                            />
                            <CardImg
                              src={`${process.env.PUBLIC_URL}/cards/${card}.svg`}
                              alt={card}
                              style={{ border: '2px solid #1976d2' }}
                            />
                          </Box>
                        ))}
                      </>
                    )}
                    {session.buyGameDetails.finalCard && (
                      <>
                        <SwapHorizIcon sx={{ color: 'success.main', mx: 0.5, mb: 2 }} />
                        <CardImg
                          src={`${process.env.PUBLIC_URL}/cards/${session.buyGameDetails.finalCard}.svg`}
                          alt={session.buyGameDetails.finalCard}
                          style={{ border: '2px solid #4caf50' }}
                        />
                      </>
                    )}
                  </Stack>
                </Stack>
              </Grid>
            </>
          )}

          <Grid item sx={{ display: 'flex' }}>
            <Divider orientation="vertical" flexItem />
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2.5} alignItems="center">
                <PaymentsIcon />
                <Typography variant="h6">Покупка игры</Typography>
              </Stack>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Купили:{' '}
                  <Typography component="span" fontWeight="bold">
                    {boxes.rows.filter((box) => box.data.buyDealerGame.cost > 0).length} из {boxes.rows.length}
                  </Typography>
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {boxes.rows.map((box) => (
                  <Chip
                    key={box.seatNumber}
                    label={`Бокс №${box.seatNumber}`}
                    size="small"
                    color={box.data.buyDealerGame.cost > 0 ? 'success' : 'default'}
                    variant={box.data.buyDealerGame.cost > 0 ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>

      <Grid container spacing={1.75} sx={{ mt: 2 }}>
        {boxes.rows.map((box) => {
          const getOutcomeConfig = (outcome: string) => {
            switch (outcome) {
              case 'win':
                return { label: 'Выиграл', color: 'success' as const }
              case 'lose':
                return { label: 'Проиграл', color: 'error' as const }
              case 'tie':
                return { label: 'Ничья', color: 'warning' as const }
              case 'noGame':
                return { label: 'Нет игры', color: 'default' as const }
              case 'pass':
                return { label: 'Пас', color: 'error' as const }
              default:
                return { label: 'Нет данных', color: 'default' as const }
            }
          }

          const outcomeConfig = getOutcomeConfig(box.data.result.outcome)

          const combinationText = ['', 'HIGH_CARD'].includes(box.data.hand.handName)
            ? '—'
            : (() => {
                const name =
                  HAND_COMBINATIONS_LABELS.ru[box.data.hand.handName as keyof typeof HAND_COMBINATIONS_LABELS.ru]
                const multiplier =
                  box.data.result?.main?.multiplier ||
                  game?.payout?.data?.find((p) => p.combination === box.data.hand.handName)?.multiplier

                return `${name} ${multiplier}:1`
              })()

          const combinationExtraText =
            box.data.hand.extra &&
            (() => {
              const name =
                HAND_COMBINATIONS_LABELS.ru[box.data.hand.extra.handName as keyof typeof HAND_COMBINATIONS_LABELS.ru]

              const multiplier =
                box.data.result.extra?.multiplier ||
                game?.payout?.data?.find((p) => p.combination === box.data.hand.extra?.handName)?.multiplier

              return `${name} ${multiplier}:1`
            })()

          return (
            <Grid item xs={12} md={6} key={box.seatNumber}>
              <CardContent component={Paper}>
                <Stack spacing={2}>
                  {/* === */}
                  <Stack direction="row" spacing={2.5} alignItems="center">
                    <PersonIcon />
                    <Typography variant="h5" fontWeight="bold">
                      Бокс №{box.seatNumber}
                    </Typography>
                    <Chip label={outcomeConfig.label} color={outcomeConfig.color} size="small" />
                  </Stack>

                  {box.data.player?.idHex && <BoxPlayerLogin playerId={box.data.player.idHex} />}

                  <Divider sx={{ my: 1.5 }} />

                  <Stack direction="row" spacing={2.5} alignItems="center">
                    <PaymentsIcon />
                    <Typography variant="h6">Ставки</Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={
                        <>
                          Бонус: <strong>{box.data.bonus.amount.toLocaleString()}</strong>
                          <DiamondIcon sx={diamondIconSx} fontSize="small" />
                        </>
                      }
                      size="medium"
                      color="default"
                      variant="filled"
                    />
                    <Chip
                      label={
                        <>
                          Анте: <strong>{box.data.ante.toLocaleString()}</strong>
                          <DiamondIcon sx={diamondIconSx} fontSize="small" />
                        </>
                      }
                      size="medium"
                      color="default"
                      variant="filled"
                    />
                    <Chip
                      label={
                        <>
                          Бет: <strong>{box.data.bet.toLocaleString()}</strong>
                          <DiamondIcon sx={diamondIconSx} fontSize="small" />
                        </>
                      }
                      size="medium"
                      color="default"
                      variant="filled"
                    />
                    <Chip
                      label={
                        <>
                          Страховка: <strong>{box.data.insurance.cost.toLocaleString()}</strong>
                          <DiamondIcon sx={diamondIconSx} fontSize="small" />
                        </>
                      }
                      size="medium"
                      color="default"
                      variant="filled"
                    />
                  </Stack>

                  {/* === */}

                  <Divider sx={{ my: 1.5 }} />

                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2.5} alignItems="center">
                      <VerifiedIcon />
                      <Typography variant="h6">Результат</Typography>
                    </Stack>

                    <Grid
                      container
                      spacing={2}
                      sx={{
                        mt: '0! important',
                      }}>
                      {/* Основная игра */}
                      <Grid item xs={12} sm={4}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Комбинация:
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5, mb: 1.5 }}>
                            <div>{combinationText}</div>
                            <div>{combinationExtraText}</div>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Выплата:
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              sx={{
                                color: getNetWinColor(box.data.result.payout),
                              }}>
                              {box.data.result.payout > 0 ? '+' : ''}
                              {box.data.result.payout.toLocaleString()}
                            </Typography>
                            <DiamondIcon sx={diamondIconSx} fontSize="small" />
                          </Stack>
                        </Box>
                      </Grid>

                      <Grid item sx={{ display: 'flex' }}>
                        <Divider orientation="vertical" flexItem />
                      </Grid>

                      {/* Бонус */}
                      <Grid item xs={12} sm>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Бонус:
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" sx={{ mt: 0.5, mb: 1.5 }}>
                            {box.data.bonus.result === 'none' ? '—' : box.data.bonus.result}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Выплата:
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              sx={{
                                color: getNetWinColor(box.data.bonus.payout),
                              }}>
                              {box.data.bonus.payout > 0 ? '+' : ''}
                              {box.data.bonus.payout.toLocaleString()}
                            </Typography>
                            <DiamondIcon sx={diamondIconSx} fontSize="small" />
                          </Stack>
                        </Box>
                      </Grid>

                      <Grid item sx={{ display: 'flex' }}>
                        <Divider orientation="vertical" flexItem />
                      </Grid>

                      {/* Страховка */}
                      <Grid item xs={12} sm>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Страховка:
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" sx={{ mt: 0.5, mb: 1.5 }}>
                            {box.data.insurance.result === 'none' ? '—' : box.data.insurance.result}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Выплата:
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              sx={{
                                color: getNetWinColor(box.data.insurance.payout),
                              }}>
                              {box.data.insurance.payout > 0 ? '+' : ''}
                              {box.data.insurance.payout.toLocaleString()}
                            </Typography>
                            <DiamondIcon sx={diamondIconSx} fontSize="small" />
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>

                    <Grid container spacing={2} mb={2}>
                      {/* Финальные карты */}
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {box.data.hand.cards.map((card) => (
                            <CardImg
                              key={`hand-${box.seatNumber}-${card}`}
                              src={`${process.env.PUBLIC_URL}/cards/${card}.svg`}
                              alt={card}
                              width={41}
                              height={65}
                            />
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                </Stack>

                {/* === Секция Обмен / Покупка === */}
                <Divider sx={{ my: 1.5 }} />

                <Grid container spacing={2}>
                  {/* Обмен карт */}
                  <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={2.5} alignItems="center" mb={1.5}>
                      <SwapHorizIcon />
                      <Typography variant="h6">Обмен карт</Typography>
                    </Stack>

                    <Stack spacing={2}>
                      {/* Стоимость */}
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Ставка:
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body1" fontWeight="bold">
                            {box.data.switchCards.cost.toLocaleString()}
                          </Typography>
                          <DiamondIcon sx={diamondIconSx} fontSize="small" />
                        </Stack>
                      </Stack>

                      {box.data.switchCards.cost > 0 && (
                        <>
                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              Отданные карты:
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {box.data.switchCards.switchedCards.map((card) => (
                                <CardImg
                                  key={`switched-${box.seatNumber}-${card}`}
                                  src={`${process.env.PUBLIC_URL}/cards/${card}.svg`}
                                  alt={card}
                                />
                              ))}
                            </Stack>
                          </Stack>

                          {/* Полученные карты */}
                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              Полученные карты:
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {box.data.switchCards.newCards.map((card) => (
                                <CardImg
                                  key={`new-${box.seatNumber}-${card}`}
                                  src={`${process.env.PUBLIC_URL}/cards/${card}.svg`}
                                  alt={card}
                                />
                              ))}
                            </Stack>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item sx={{ display: 'flex' }}>
                    <Divider orientation="vertical" flexItem />
                  </Grid>

                  {/* Покупка 6-й карты */}
                  <Grid item xs={12} md>
                    <Stack direction="row" spacing={2.5} alignItems="center" mb={1.5}>
                      <ShoppingCartIcon />
                      <Typography variant="h6">Покупка 6-й карты</Typography>
                    </Stack>

                    <Stack spacing={2}>
                      {/* Стоимость */}
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Ставка:
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body1" fontWeight="bold">
                            {box.data.buySixCard.cost.toLocaleString()}
                          </Typography>
                          <DiamondIcon sx={diamondIconSx} fontSize="small" />
                        </Stack>
                      </Stack>

                      {box.data.buySixCard.newCard && (
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            Получил карту:
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            <CardImg
                              src={`${process.env.PUBLIC_URL}/cards/${box.data.buySixCard.newCard}.svg`}
                              alt={box.data.buySixCard.newCard}
                            />
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                  </Grid>
                </Grid>

                {box.data?.playerFinancials && (
                  <>
                    <Divider sx={{ my: 1.5 }} />

                    <Stack direction="row" spacing={2.5} alignItems="center" mb={2.5}>
                      <MonetizationOnIcon />
                      <Stack>
                        <Typography variant="h6">Финансы</Typography>
                      </Stack>
                    </Stack>

                    <Grid container spacing={2}>
                      {/* Финансовые операции */}
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          {/* Первая строка */}
                          {/* Бонус */}
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Бонус"
                              value={formatFinancialValue(box.data.playerFinancials.bonus.netWin)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: 'text.primary' }}>
                                      {
                                        symbolCurrency[
                                          box.data.playerFinancials.currency as keyof typeof symbolCurrency
                                        ]
                                      }
                                    </Typography>
                                  </InputAdornment>
                                ),
                                sx: {
                                  color: getNetWinColor(box.data.playerFinancials.bonus.netWin),
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                },
                              }}
                            />
                          </Grid>

                          {/* Анте */}
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Анте"
                              value={formatFinancialValue(-box.data.playerFinancials.ante)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: 'text.primary' }}>
                                      {
                                        symbolCurrency[
                                          box.data.playerFinancials.currency as keyof typeof symbolCurrency
                                        ]
                                      }
                                    </Typography>
                                  </InputAdornment>
                                ),
                                sx: {
                                  color: getNetWinColor(-box.data.playerFinancials.ante),
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                },
                              }}
                            />
                          </Grid>

                          {/* Бет */}
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Бет"
                              value={formatFinancialValue(-box.data.playerFinancials.bet)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: 'text.primary' }}>
                                      {
                                        symbolCurrency[
                                          box.data.playerFinancials.currency as keyof typeof symbolCurrency
                                        ]
                                      }
                                    </Typography>
                                  </InputAdornment>
                                ),
                                sx: {
                                  color: getNetWinColor(-box.data.playerFinancials.bet),
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                },
                              }}
                            />
                          </Grid>

                          {/* Вторая строка */}
                          {/* Страховка */}
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label="Страховка"
                              value={formatFinancialValue(box.data.playerFinancials.insurance.netWin)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: 'text.primary' }}>
                                      {
                                        symbolCurrency[
                                          box.data.playerFinancials.currency as keyof typeof symbolCurrency
                                        ]
                                      }
                                    </Typography>
                                  </InputAdornment>
                                ),
                                sx: {
                                  color: getNetWinColor(box.data.playerFinancials.insurance.netWin),
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                },
                              }}
                            />
                          </Grid>

                          {/* Обмен */}
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label="Обмен"
                              value={formatFinancialValue(-box.data.playerFinancials.switchCards.cost)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: 'text.primary' }}>
                                      {
                                        symbolCurrency[
                                          box.data.playerFinancials.currency as keyof typeof symbolCurrency
                                        ]
                                      }
                                    </Typography>
                                  </InputAdornment>
                                ),
                                sx: {
                                  color: getNetWinColor(-box.data.playerFinancials.switchCards.cost),
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                },
                              }}
                            />
                          </Grid>

                          {/* Покупка 6 */}
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label="Покупка 6-й карты"
                              value={formatFinancialValue(-box.data.playerFinancials.buySixCard.cost)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: 'text.primary' }}>
                                      {
                                        symbolCurrency[
                                          box.data.playerFinancials.currency as keyof typeof symbolCurrency
                                        ]
                                      }
                                    </Typography>
                                  </InputAdornment>
                                ),
                                sx: {
                                  color: getNetWinColor(-box.data.playerFinancials.buySixCard.cost),
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                },
                              }}
                            />
                          </Grid>

                          {/* Покупка игры дилера */}
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label="Покупка игры дилеру"
                              value={formatFinancialValue(-box.data.playerFinancials.buyDealerGame.cost)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: 'text.primary' }}>
                                      {
                                        symbolCurrency[
                                          box.data.playerFinancials.currency as keyof typeof symbolCurrency
                                        ]
                                      }
                                    </Typography>
                                  </InputAdornment>
                                ),
                                sx: {
                                  color: getNetWinColor(-box.data.playerFinancials.buyDealerGame.cost),
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Разделитель */}
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      {/* Выплаты */}
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          {/* Выплата по комбинации */}
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Выплата по комбинации"
                              value={formatFinancialValue(box.data.playerFinancials.result.combinationPayout)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: 'text.primary' }}>
                                      {
                                        symbolCurrency[
                                          box.data.playerFinancials.currency as keyof typeof symbolCurrency
                                        ]
                                      }
                                    </Typography>
                                  </InputAdornment>
                                ),
                                sx: {
                                  color: getNetWinColor(box.data.playerFinancials.result.combinationPayout),
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                },
                              }}
                            />
                          </Grid>

                          {/* Выплата по бонусу */}
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Выплата по бонусу"
                              value={formatFinancialValue(box.data.playerFinancials.bonus.payout)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: 'text.primary' }}>
                                      {
                                        symbolCurrency[
                                          box.data.playerFinancials.currency as keyof typeof symbolCurrency
                                        ]
                                      }
                                    </Typography>
                                  </InputAdornment>
                                ),
                                sx: {
                                  color: getNetWinColor(box.data.playerFinancials.bonus.payout),
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                },
                              }}
                            />
                          </Grid>

                          {/* Выплата по страховке */}
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Выплата по страховке"
                              value={formatFinancialValue(box.data.playerFinancials.insurance.payout)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: 'text.primary' }}>
                                      {
                                        symbolCurrency[
                                          box.data.playerFinancials.currency as keyof typeof symbolCurrency
                                        ]
                                      }
                                    </Typography>
                                  </InputAdornment>
                                ),
                                sx: {
                                  color: getNetWinColor(box.data.playerFinancials.insurance.payout),
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
              </CardContent>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
})

export default RpGameSessionDetails
